import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { environment } from '../../../environments/environment';
import { auth, firestore } from '../../../../../../libs/firebase-service/src';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import { FaTruck } from 'react-icons/fa';
import { GiPaperBagOpen } from 'react-icons/gi';
import {
  formatToUTC,
  formatToLocalTime,
  formatPriceFromCents,
} from '../../Utils/UtilsFunctions';
import OrderStatusBadge, {
  getOrderStatus,
  mapSquareStatusToCustomStatus,
} from './OrderStatusBadge';
const FattyBattyCard = ({ order, order_id, order_status, onViewDetails }) => {
  const [t] = useTranslation();
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  // Keep a local copy of the order so we can update UI immediately after actions
  const [currentOrder, setCurrentOrder] = useState(order);
  const [statusLoaded, setStatusLoaded] = useState(false);

  if (
    !order ||
    order_id === undefined ||
    order_id === null ||
    order_status === undefined ||
    order_status === null
  ) {
    return null;
  }

  // Function to map Square fulfillment states to our custom status (same as OrderHistoryDetails)
  const mapSquareStatusToCustomStatus = (fulfillments) => {
    if (!fulfillments || fulfillments.length === 0) {
      return order_status; // Fallback to existing order_status
    }

    // Get the first fulfillment (assuming single fulfillment per order)
    const fulfillment = fulfillments[0];
    const squareState = fulfillment?.state;

    switch (squareState) {
      case 'PROPOSED':
        return 'new'; // Order Placed
      case 'RESERVED':
        return 'inprogress'; // In Progress
      case 'PREPARED':
        return 'ready_to_pickup'; // Ready
      case 'COMPLETED':
        return 'delivered'; // Delivered
      case 'CANCELED':
        return 'cancelled'; // Cancelled
      case 'FAILED':
        return 'inprogress'; // In Progress (or could be 'failed' if you add this state)
      default:
        return order_status; // Fallback to existing order_status
    }
  };

  // Get the mapped status from Square fulfillments using local state
  const activeOrder = currentOrder || order;
  const fulfillments = activeOrder?.fulfillments || [];
  
  // Check if refunds object exists
  const hasRefunds = activeOrder?.refunds && Object.keys(activeOrder.refunds).length > 0;
  
  // prefer order.order_delivery_status when present
  const mappedStatus =
    getOrderStatus(activeOrder) ||
    mapSquareStatusToCustomStatus(fulfillments, order_status);

  // Live-listen for order changes and update immediately (keeps mappedStatus fresh)
  useEffect(() => {
    if (!order_id) return;
    try {
      // Initial get to avoid flicker
      firestore
        .collection('orders')
        .doc(order_id)
        .get()
        .then((doc) => {
          if (doc.exists) {
            const data = doc.data();
            setCurrentOrder(data);
          }
          setStatusLoaded(true);
        })
        .catch((err) => {
          // Error handled silently
          setStatusLoaded(true);
        });

      const unsubscribe = firestore
        .collection('orders')
        .doc(order_id)
        .onSnapshot(
          (doc) => {
            if (doc.exists) {
              const data = doc.data();
              setCurrentOrder(data);
            }
            setStatusLoaded(true);
          },
          (err) => {
            // Error handled silently
            setStatusLoaded(true);
          }
        );
      return () => {
        if (typeof unsubscribe === 'function') unsubscribe();
      };
    } catch (e) {
      // Error handled silently
      setStatusLoaded(true);
    }
  }, [order_id]);

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'cancelled':
        return 'Cancelled';
      case 'delivered':
        return 'Delivered';
      case 'pending':
        return 'Pending';
      case 'confirmed':
        return 'Confirmed';
      case 'inprogress':
        return 'In Progress';
      case 'new':
        return 'Order Placed';
      case 'ready_to_pickup':
        return 'Ready to Pickup';
      case 'scheduled':
        return 'Scheduled';
      default:
        return status;
    }
  };

  function getStatusStyles(status) {
    switch (status) {
      case 'cancelled':
        return {
          bgColor: '#FFEAEA',
          textColor: '#FF3B30',
          dotColor: '#FF3B30',
        };
      case 'delivered':
        return {
          bgColor: '#E6F7EE',
          textColor: '#34C759',
          dotColor: '#34C759',
        };
      case 'inprogress':
      case 'new':
      case 'confirmed':
      case 'ready_to_pickup':
      case 'scheduled':
        return {
          bgColor: '#EAF3FF',
          textColor: '#007AFF',
          dotColor: '#007AFF',
        };
      default:
        return {
          bgColor: '#F3F6F9',
          textColor: '#404040',
          dotColor: '#404040',
        };
    }
  }
  // Friendly payment method formatter - handles array (e.g. ["GIFT_CARD", "GOOGLE_PAY"]) or string
  const formatPaymentMethod = (pm) => {
    if (!pm) return 'N/A';
    const methods = Array.isArray(pm) ? pm : [pm];
    const normalized = methods.map((m) => String(m).toUpperCase().trim()).filter(Boolean);
    if (normalized.length === 0) return 'N/A';

    const hasGiftCard = normalized.some((m) => m === 'GIFT_CARD' || m.includes('GIFT'));
    const hasCard = normalized.some((m) => m === 'CARD' || m === 'CARD_ON_FILE');
    const hasGooglePay = normalized.some((m) => m.includes('GOOGLE'));
    const hasApplePay = normalized.some((m) => m.includes('APPLE'));
    const hasCashApp = normalized.some((m) => m.includes('CASH'));

    if (hasGiftCard && hasCard) return 'Card + Gift Card';
    if (hasGiftCard && hasGooglePay) return 'Gift Card + Google Pay';
    if (hasGiftCard && hasApplePay) return 'Gift Card + Apple Pay';
    if (hasGiftCard && hasCashApp) return 'Gift Card + Cash App Pay';

    if (normalized.includes('CARD_ON_FILE')) return 'Card (On File)';
    if (hasCard) return 'Card';
    if (hasGiftCard) return 'Gift Card';
    if (hasGooglePay) return 'Google Pay';
    if (hasApplePay) return 'Apple Pay';
    if (hasCashApp) return 'Cash App Pay';

    const fallback = normalized[0];
    return String(fallback)
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };
  // Format gift card number as **** **** **** last4
  const formatGiftCardMasked = (last4) => {
    if (!last4) return '**** **** **** ****';
    const l4 = String(last4).slice(-4);
    return `**** **** **** ${l4}`;
  };
  // Format different scheduled_at shapes (Firestore Timestamp, ISO string, Date)

  const handleCancelOrder = async () => {
    setLoading(true);

    let apiResponse = null;
    let apiData = null;

    try {
      const user = auth?.currentUser;
      if (user) {
        const token = await user.getIdToken(true);
        if (
          token &&
          typeof token === 'string' &&
          token.split('.').length === 3
        ) {
          const headers = {
            'Content-Type': 'application/json',
            Authorization: ` ${token}`,
          };

          // Determine paymentId based on allPayments array
          let paymentId;
          if (order?.allPayments && Array.isArray(order.allPayments)) {
            if (order.allPayments.length === 2) {
              // Two payments: send as array
              paymentId = order.allPayments
                .map((p) => {
                  // Check if p is a string or an object
                  const id = typeof p === 'string' ? p : p.id || p.payment_id;
                  return id;
                })
                .filter(Boolean);

              // If after filtering we still have 2 items, keep as array, otherwise make single value
              if (paymentId.length === 0) {
                paymentId = null;
              } else if (paymentId.length === 1) {
                paymentId = paymentId[0];
              }
            } else if (order.allPayments.length === 1) {
              // Single payment: send as string
              const payment = order.allPayments[0];
              paymentId =
                typeof payment === 'string'
                  ? payment
                  : payment.id || payment.payment_id;
            } else {
              // Fallback to old logic if allPayments is empty
              paymentId =
                order?.payment_details?.payment_id ||
                order.payment_id ||
                order.paymentId;
            }
          } else {
            // Fallback to old logic if allPayments doesn't exist
            paymentId =
              order?.payment_details?.payment_id ||
              order.payment_id ||
              order.paymentId;
          }

          // NEVER send empty paymentId
          if (
            !paymentId ||
            (Array.isArray(paymentId) && paymentId.length === 0)
          ) {
            toast.error('Cannot process refund: Payment ID not found');
            setLoading(false);
            setOpenCancelDialog(false);
            return;
          }

          const refundAmount =
            activeOrder?.has_giftcard_payment && (activeOrder?.other_payment_amount_used ?? 0) > 0
              ? parseFloat(activeOrder.other_payment_amount_used)
              : parseFloat(order.order_total || 0);
          const requestBody = {
            orderId: order_id,
            paymentId,
            refundAmount,
            uid: user.uid,
            storeId: order.store?.id || order.storeId || '',
          };

          try {
            apiResponse = await fetch(
              `${environment?.apiUrl}/startRefund?restaurantId=${environment.restaurantId}`,
              {
                method: 'POST',
                headers,
                body: JSON.stringify(requestBody),
              }
            );
            try {
              apiData = await apiResponse.json();
            } catch (_) {
              apiData = null;
            }
          } catch (error) {
            // Error handled silently
          }
        }
      }
    } catch (error) {
      // Error handled silently
    }

    // After 20 seconds, show success if API succeeded; otherwise show error
    setTimeout(() => {
      if (
        apiResponse?.ok &&
        (apiData?.success === true || apiData?.success === 'true')
      ) {
        toast.success('Refund initiated successfully');
        setLoading(false);
        setOpenCancelDialog(false);

        // Fetch updated order status from Firebase (success branch)
        (async () => {
          try {
            const orderDoc = await firestore
              .collection('orders')
              .doc(order_id)
              .get();

            if (orderDoc.exists) {
              const updatedOrderData = orderDoc.data();
              setCurrentOrder(updatedOrderData);
            } else {
              // Fallback: update local state optimistically
              const updatedFulfillments =
                activeOrder?.fulfillments && activeOrder.fulfillments.length > 0
                  ? [
                      { ...activeOrder.fulfillments[0], state: 'CANCELED' },
                      ...activeOrder.fulfillments.slice(1),
                    ]
                  : [{ state: 'CANCELED' }];

              setCurrentOrder((prev) => ({
                ...(prev || activeOrder),
                fulfillments: updatedFulfillments,
                order_status: 'cancelled',
                order_delivery_status: 'cancelled',
              }));
            }
          } catch (err) {
            // Error handled silently
            // Update local state optimistically as fallback
            const updatedFulfillments =
              activeOrder?.fulfillments && activeOrder.fulfillments.length > 0
                ? [
                    { ...activeOrder.fulfillments[0], state: 'CANCELED' },
                    ...activeOrder.fulfillments.slice(1),
                  ]
                : [{ state: 'CANCELED' }];

            setCurrentOrder((prev) => ({
              ...(prev || activeOrder),
              fulfillments: updatedFulfillments,
              order_status: 'cancelled',
              order_delivery_status: 'cancelled',
            }));
          }
        })();
      } else {
        toast.error(
          (apiData && (apiData.error || apiData.shortMessage)) ||
            'Failed to initiate refund'
        );
        setLoading(false);
        setOpenCancelDialog(false);
      }
    }, 20000);
  };

  const canCancelOrder = () => {
    // Don't allow cancelling if refunds object exists (order already cancelled and refund initiated)
    if (activeOrder?.refunds && Object.keys(activeOrder.refunds).length > 0) {
      return false;
    }

    // Only allow cancelling when the order is still in the initial "proposed" state.
    // Use the live `activeOrder` (local/up-to-date) and check common fallback fields
    // so we're consistent with how `mappedStatus` is derived.
    const state =
      (activeOrder?.fulfillments && activeOrder.fulfillments[0]?.state) ||
      activeOrder?.order_delivery_status ||
      activeOrder?.order_status ||
      '';

    if (!state) return false;

    return String(state).toLowerCase() === 'proposed';
  };

  return (
    <Stack
      sx={{
        borderRadius: '12px',
        padding: '16px',
        backgroundColor: '#F3F6F9',
        width: '100%',
        gap: 2,
        border: '1px solid #E5E8EB',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{}}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#404040',
            fontSize: '18px',
            fontWeight: 600,
            fontFamily: 'Jost, sans-serif',
            textTransform: 'capitalize',
          }}
        >
          order no:{' '}
          <span style={{ color: '#172B4D' }}>{order?.shortId || order_id}</span>
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="text"
            startIcon={<VisibilityIcon fontSize="small" />}
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: 'Jost, sans-serif',
              textTransform: 'capitalize',
              color: environment.mainColor,
              padding: '8px 16px',
              borderRadius: '8px',
              transition: 'all 0.2s ease-in-out',
              border: `1px solid transparent`,
              '&:hover': {
                backgroundColor: 'rgba(255, 107, 53, 0.08)',
                border: `1px solid ${environment.mainColor}`,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(255, 107, 53, 0.15)',
              },
            }}
            onClick={() => onViewDetails && onViewDetails(order)}
          >
            view details
          </Button>

          {canCancelOrder() && mappedStatus !== 'cancelled' && !hasRefunds && (
            <Button
              variant="text"
              startIcon={<CancelIcon fontSize="small" />}
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'Jost, sans-serif',
                textTransform: 'capitalize',
                color: '#FF3B30',
                padding: '8px 16px',
                borderRadius: '8px',
                transition: 'all 0.2s ease-in-out',
                border: `1px solid transparent`,
                '&:hover': {
                  backgroundColor: 'rgba(255, 59, 48, 0.08)',
                  border: `1px solid #FF3B30`,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(255, 59, 48, 0.15)',
                },
                '&:disabled': {
                  color: '#999',
                  backgroundColor: 'transparent',
                  cursor: 'not-allowed',
                },
              }}
              onClick={() => setOpenCancelDialog(true)}
              disabled={loading}
            >
              {loading ? 'canceling...' : 'cancel order'}
            </Button>
          )}
        </Stack>
      </Stack>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>
          <Typography
            variant="p"
            sx={{
              color: '#3C4242',
              fontSize: '14px',
              fontFamily: 'Jost, sans-serif',
            }}
          >
            Order Date :
            <span style={{ color: '#333333' }}>
              {' '}
              {formatToLocalTime(order?.created_at)}{' '}
            </span>
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
          sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}
        >
          <Typography
            variant="p"
            sx={{
              color: '#3C4242',
              fontSize: '14px',
              fontFamily: 'Jost, sans-serif',
            }}
          >
            Order Status :
          </Typography>
          <Box sx={{ ml: 1 }}>
            {statusLoaded ? (
              <OrderStatusBadge
                order={activeOrder}
                mappedStatus={mappedStatus}
              />
            ) : null}
          </Box>
        </Grid>
        {activeOrder?.deliveryOption === 'pickup' ? (
          <Grid item xs={12} sm={4}>
            <Typography
              variant="p"
              sx={{
                color: '#3C4242',
                fontSize: '14px',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Picked Up:{' '}
              <span style={{ color: '#333333' }}>
                {' '}
                {order?.store?.businessName}{' '}
              </span>
            </Typography>
          </Grid>
        ) : (
          <Grid item xs={12} sm={4}>
            <Typography
              variant="p"
              sx={{
                color: '#3C4242',
                fontSize: '14px',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Delivery address:{' '}
              <span style={{ color: '#333333', display: 'inline-block' }}>
                {/** Render delivery_address object in a friendly format */}
                {activeOrder?.delivery_address ? (
                  <span>
                    <div style={{ lineHeight: 1.2 }}>
                      {activeOrder.delivery_address.primaryLine ||
                        activeOrder.delivery_address.displayName}
                    </div>
                    <div
                      style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}
                    >
                      {activeOrder.delivery_address.city || ''}
                      {activeOrder.delivery_address.state
                        ? `, ${activeOrder.delivery_address.state}`
                        : ''}
                      {activeOrder.delivery_address.zipCode ? (
                        <span>
                          {' '}
                          <strong>
                            {activeOrder.delivery_address.zipCode}
                          </strong>
                        </span>
                      ) : null}
                    </div>
                  </span>
                ) : (
                  <span>N/A</span>
                )}
              </span>
            </Typography>
          </Grid>
        )}

        {/* Second Row */}
        {!activeOrder?.deliveryOption === 'pickup' && (
          <Grid
            item
            xs={12}
            sm={4}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography
              sx={{
                color: '#3C4242',
                fontSize: '14px',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Estimated Delivery Date :{' '}
              <span style={{ color: '#333333' }}>
                {estimated_delivery_date || 'Not Available'}
              </span>
            </Typography>
          </Grid>
        )}
        {activeOrder?.is_scheduling && (
          <Grid
            item
            xs={12}
            sm={4}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography
              sx={{
                color: '#3C4242',
                fontSize: '14px',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Scheduled At :{' '}
              <span style={{ color: '#333333' }}>
                {formatToLocalTime(activeOrder?.scheduled_at)}
              </span>
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={4}>
          <Stack direction="row" alignItems="center" sx={{}} gap={1}>
            <Typography
              variant="p"
              sx={{
                color: '#3C4242',
                fontSize: '14px',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Order Type :
            </Typography>
            <span
              style={{
                color: '#333333',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {/* Icon indicator for pickup vs delivery */}
              {order?.deliveryOption === 'pickup' ? (
                <GiPaperBagOpen
                  style={{
                    color: environment?.mainColor || '#172B4D',
                    fontSize: 16,
                  }}
                />
              ) : (
                <FaTruck
                  style={{
                    color: environment?.mainColor || '#172B4D',
                    fontSize: 16,
                  }}
                />
              )}
              <span style={{ textTransform: 'capitalize' }}>
                {t(activeOrder?.deliveryOption) || activeOrder?.deliveryOption}
              </span>
            </span>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography
            variant="p"
            sx={{
              color: '#3C4242',
              fontSize: '14px',
              fontFamily: 'Jost, sans-serif',
              textTransform: 'capitalize',
            }}
          >
            Payment Method :
            <span style={{ color: '#333333' }}>
              {' '}
              {formatPaymentMethod(activeOrder?.payment_method)}
            </span>
          </Typography>
        </Grid>
        {activeOrder?.has_giftcard_payment && activeOrder?.giftcard_details && (
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '10px 14px',
                backgroundColor: '#E8F5E9',
                borderRadius: '10px',
                border: '1px solid #C8E6C9',
                mt: 1,
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#2E7D32', fontFamily: 'Jost, sans-serif', fontWeight: 600 }}>
                🎁 Gift Card:
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#172B4D', fontFamily: 'Jost, sans-serif', letterSpacing: '0.05em' }}>
                {formatGiftCardMasked(activeOrder.giftcard_details?.cardDetails?.card?.last4)}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#2E7D32', fontFamily: 'Jost, sans-serif', fontWeight: 600 }}>
                -{environment.currency}
                {formatPriceFromCents(activeOrder.giftcard_amount_used || activeOrder.giftcard_details?.amountMoney?.amount || 0)}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
      {activeOrder?.is_scheduling &&
        activeOrder?.deliveryOption === 'delivery' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              marginTop: '12px',
              padding: '12px 14px',
              backgroundColor: '#FEF3C7',
              borderRadius: '10px',
              borderLeft: `4px solid ${environment.warningColor || '#F59E0B'}`,
              gridColumn: { xs: 'span 1', md: '1 / -1' },
              width: '100%',
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: environment.warningColor || '#F59E0B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: 700,
                  fontFamily: 'Jost',
                }}
              >
                i
              </Typography>
            </Box>
            <Typography
              sx={{
                color: '#92400E',
                fontFamily: 'Jost',
                fontWeight: 500,
                fontSize: '13px',
                lineHeight: 1.6,
                flex: 1,
              }}
            >
              <strong>Delivery Note:</strong> An additional 45 min - 1 hour will
              be added to your scheduled time to ensure fresh delivery
            </Typography>
          </Box>
        )}
      {/* Dasher / Delivery person info */}
      {activeOrder?.delivery?.dasher && (
        <Grid container spacing={1} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                src={activeOrder.delivery.dasher.profile_image_url || ''}
                alt={`${activeOrder.delivery.dasher.first_name || ''} ${
                  activeOrder.delivery.dasher.last_name || ''
                }`}
                sx={{ width: 48, height: 48, bgcolor: '#EAF3FF' }}
              >
                {!activeOrder.delivery.dasher.profile_image_url &&
                  `${(activeOrder.delivery.dasher.first_name || '').charAt(
                    0
                  )}${(activeOrder.delivery.dasher.last_name || '').charAt(
                    0
                  )}`.toUpperCase()}
              </Avatar>
              <div>
                <Typography
                  variant="p"
                  sx={{
                    color: '#3C4242',
                    fontSize: '14px',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  {`${activeOrder.delivery.dasher.first_name || ''} ${
                    activeOrder.delivery.dasher.last_name || ''
                  }`}
                </Typography>
                <Typography variant="p" sx={{ color: '#6B7280', fontSize: 13 }}>
                  {activeOrder.delivery.dasher
                    .dasher_phone_number_for_customer ||
                    activeOrder.delivery.dasher.phone_number ||
                    'No phone available'}
                </Typography>
              </div>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={8}>
            <Typography
              variant="p"
              sx={{
                color: '#3C4242',
                fontSize: '14px',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Vehicle:
              <span style={{ color: '#333333', marginLeft: 8 }}>
                {activeOrder.delivery.dasher.vehicle
                  ? [
                      activeOrder.delivery.dasher.vehicle.make,
                      activeOrder.delivery.dasher.vehicle.model,
                      activeOrder.delivery.dasher.vehicle.year,
                      activeOrder.delivery.dasher.vehicle.color,
                    ]
                      .filter(Boolean)
                      .join(' ') || 'N/A'
                  : 'N/A'}
              </span>
            </Typography>
          </Grid>
        </Grid>
      )}

      {/* Cancel Order Confirmation Dialog */}
      <Dialog
        open={openCancelDialog}
        onClose={() => !loading && setOpenCancelDialog(false)}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '24px',
            padding: '8px',
            boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle
          id="cancel-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            paddingBottom: '12px',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <Box
            sx={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: '#FFEAEA',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CancelIcon sx={{ color: '#FF3B30', fontSize: 28 }} />
          </Box>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#172B4D',
              fontFamily: 'Jost, sans-serif',
            }}
          >
            Cancel Order Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', minHeight: '120px' }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
              }}
            >
              <CircularProgress size={50} sx={{ color: '#FF3B30', mb: 2 }} />
              <DialogContentText
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  color: '#404040',
                  textAlign: 'center',
                  fontSize: '16px',
                }}
              >
                Processing cancellation...
              </DialogContentText>
            </Box>
          ) : (
            <>
              <DialogContentText
                id="cancel-dialog-description"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  color: '#4B5563',
                  my: 2,
                  fontSize: '16px',
                  lineHeight: 1.6,
                }}
              >
                Are you sure you want to cancel order{' '}
                <strong style={{ color: '#172B4D' }}>
                  #{activeOrder?.shortId || order_id}
                </strong>
                ?
              </DialogContentText>
              <Box
                sx={{
                  backgroundColor: '#FFF9F5',
                  border: '1px solid #FFE5D9',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'start',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: '#FFE5D9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ fontSize: '18px' }}>💰</Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      color: '#172B4D',
                      fontSize: '15px',
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    Refund Information
                  </Typography>
                  <DialogContentText
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      color: '#666',
                      fontSize: '14px',
                      lineHeight: 1.5,
                    }}
                  >
                    A refund
                    will be initiated and processed within{' '}
                    <strong>3-5 business days</strong>.
                  </DialogContentText>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            padding: '16px 24px',
            gap: 1,
            borderTop: '1px solid #E5E7EB',
          }}
        >
          <Button
            onClick={() => setOpenCancelDialog(false)}
            sx={{
              textTransform: 'none',
              color: '#6B7280',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '15px',
              padding: '10px 24px',
              borderRadius: '12px',
              '&:hover': {
                backgroundColor: '#F3F4F6',
              },
            }}
            disabled={loading}
          >
            Keep Order
          </Button>
          <Button
            onClick={handleCancelOrder}
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: '#FF3B30',
              color: 'white',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '15px',
              padding: '10px 32px',
              borderRadius: '12px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#FF2D20',
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: '#FFB3AE',
                color: 'white',
              },
            }}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Yes, Cancel Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default FattyBattyCard;
